// ---------------------------------------------------------------------------
// Site shell context: language, translations, settings, navigation, sections.
//
// One /api/bootstrap call supplies all of it. Nothing in the component tree
// carries hardcoded copy — every label goes through `t(key)`.
// ---------------------------------------------------------------------------

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/apiClient.js';
import { formatMoney, resolveHref, fill, pick } from '../lib/format.js';

const SiteContext = createContext(null);

const LANGUAGE_KEY = 'connix-language';

export function SiteProvider({ children }) {
  const [language, setLanguageState] = useState(() => localStorage.getItem(LANGUAGE_KEY) || 'en');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => api.get('/bootstrap'),
    staleTime: 5 * 60 * 1000,
  });

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
  }, [language, dir]);

  // Pages set their own title via usePageMeta; this only supplies the default
  // once settings arrive, and never fights the server-rendered tag.
  useEffect(() => {
    const title = data?.settings?.['seo.title'];
    if (title && document.title === '') document.title = title;
  }, [data]);

  const setLanguage = useCallback((next) => {
    localStorage.setItem(LANGUAGE_KEY, next);
    setLanguageState(next);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  }, [language, setLanguage]);

  const value = useMemo(() => {
    const dictionary = data?.translations?.[language] ?? {};
    const htmlKeys = new Set(data?.translations?.html ?? []);
    const settings = data?.settings ?? {};

    /** Translate a key. Falls back to the key itself so gaps are visible. */
    const t = (key, values) => {
      const raw = dictionary[key];
      if (raw === undefined) return key;
      return values ? fill(raw, values) : raw;
    };

    return {
      language,
      dir,
      setLanguage,
      toggleLanguage,
      isLoading,
      isError,
      refetch,
      t,
      isHtml: (key) => htmlKeys.has(key),
      pick: (pair) => pick(pair, language),
      settings,
      nav: data?.nav ?? { header: [], footer: [] },
      blocks: data?.blocks ?? [],
      block: (key) => (data?.blocks ?? []).find((entry) => entry.key === key) ?? null,
      href: (link) => resolveHref(link, settings),
      money: (amount) =>
        formatMoney(amount, language, dictionary.currencyCode ?? settings['currency.code'] ?? 'EGP', settings['currency.locale'] ?? 'en-US'),
    };
  }, [data, language, dir, isLoading, isError, refetch, setLanguage, toggleLanguage]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const context = useContext(SiteContext);
  if (!context) throw new Error('useSite must be used inside <SiteProvider>');
  return context;
}
